// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { HasDeferredApiContext, withCallDeferredApi } from "@insite/client-framework/Components/DeferredApiContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getShouldPreventCatalogLoop } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAllBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadAllBrands from "@insite/client-framework/Store/Pages/Brands/Handlers/LoadAllBrands";
import loadBrandsAlphabet from "@insite/client-framework/Store/Pages/Brands/Handlers/LoadBrandsAlphabet";
import translate from "@insite/client-framework/Translate";
import { MetaDescription, MetaKeywords } from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import Page from "@insite/mobius/Page";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const allBrandsDataView = getAllBrandsDataView(state);
    return {
        shouldLoadBrandAlphabet:
            !state.pages.brands.brandAlphabetState.value && !state.pages.brands.brandAlphabetState.isLoading,
        shouldLoadAllBrandList: !allBrandsDataView.value && !allBrandsDataView.isLoading,
        deferredApiCalls: state.context.deferredApiCalls,
    };
};

const mapDispatchToProps = {
    loadBrandsAlphabet,
    loadAllBrands,
};

// <XNG-Changes>
export interface BrandsPageStyles {
    buttons?: ClickablePresentationProps;
    backToTopButton?: InjectableCss;
    backToTopIcon?: IconPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
}

export const brandsPageStyles: BrandsPageStyles = {};
// </XNG-Changes>

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext &
    PageProps &
    HasDeferredApiContext;

// <XNG-Changes>
const styles = brandsPageStyles;
// </XNG-Changes>

class BrandsPage extends React.Component<Props> {
    // <XNG-Changes>
    state = {
        visible: false,
    };
    // </XNG-Changes>

    UNSAFE_componentWillMount() {
        const { loadBrandsAlphabet, shouldLoadBrandAlphabet, shouldLoadAllBrandList, loadAllBrands } = this.props;
        if (shouldLoadBrandAlphabet && !getShouldPreventCatalogLoop(this.props.deferredApiCalls)) {
            loadBrandsAlphabet();
        }
        if (shouldLoadAllBrandList && !getShouldPreventCatalogLoop(this.props.deferredApiCalls)) {
            loadAllBrands({
                sort: "name asc",
                select: "id,detailPagePath,name",
            });
        }
    }

    // <XNG-Changes>
    handleScroll = () => {
        const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
        const newVisible = scrollTop > 50;

        if (this.state.visible !== newVisible) {
            this.setState({ visible: newVisible });
        }
    };
    handleBackToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });

        const elementToFocus = document.getElementById("collapseButton");
        if (elementToFocus) {
            elementToFocus.focus();
        } else {
            document.getElementById("expandButton")?.focus();
        }
    };

    componentDidMount() {
        window.addEventListener("scroll", this.handleScroll, { passive: true });
    }

    componentWillUnmount() {
        window.removeEventListener("scroll", this.handleScroll);
    }
    // </XNG-Changes>

    render() {
        // <XNG-Changes>
        const { shouldLoadBrandAlphabet, shouldLoadAllBrandList } = this.props;
        // </XNG-Changes>

        return (
            <Page>
                {/* XNG-Changes: add Loading Overlay */}
                <LoadingOverlay {...styles.loadingOverlay} loading={shouldLoadAllBrandList || shouldLoadBrandAlphabet}>
                    <Zone contentId={this.props.id} zoneName="Content" />
                    <Modals />
                    {/* XNG-Changes: add Back to top section */}
                    {this.state.visible && (
                        <Clickable {...styles.buttons} onClick={this.handleBackToTop}>
                            <Icon {...styles.backToTopIcon} />
                            <Typography {...styles.backToTopButton}>{translate("Back to Top")}</Typography>
                        </Clickable>
                    )}
                </LoadingOverlay>
            </Page>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withCallDeferredApi(withIsInShell(BrandsPage))),
    definition: {
        hasEditableUrlSegment: false,
        hasEditableTitle: true,
        pageType: "System",
        // XNG-Change
        fieldDefinitions: [MetaKeywords, MetaDescription],
    },
};

export default pageModule;

export const BrandsPageContext = "BrandsPage";
